import { type Control, useFieldArray } from "react-hook-form";

import type { PropertySchemaAssociation_DefaultFragment } from "~/features/admin/propertySchemas/gqls";
import type {
  PropertySchemaAssociationFormData,
  PropertySchemaAssociationsFormDataBase,
} from "~/features/admin/propertySchemas/types";
import { buildNewAssociation } from "~/features/admin/propertySchemas/utils/buildNewAssociation";
import {
  type InheritedSchemaSummary,
  reconcileMandatoryAssociations,
} from "~/features/admin/propertySchemas/utils/mandatoryMappings";
import { toAssociationFormData } from "~/features/admin/propertySchemas/utils/mapper";

/**
 * Wraps react-hook-form's `useFieldArray` for `propertySchemaAssociations`.
 * Exposes:
 *   - `fields`, `remove`, `move` — used by the list UI
 *   - `append()` — adds a new blank row (position is normalized by the list)
 *   - `reconcileMandatorySchemas()` — keeps the mandatory entity schemas seeded, minus inherited ones
 *   - `setDefaults(...)` — replaces the field array from existing API rows
 */
export function usePropertySchemaAssociationsForm<F extends PropertySchemaAssociationsFormDataBase>(
  control: F extends PropertySchemaAssociationsFormDataBase ? Control<F> : never,
) {
  const { fields, append, remove, replace, insert, move } = useFieldArray({
    control,
    name: "propertySchemaAssociations",
  });

  return {
    fields,
    append: () => append(buildNewAssociation() as PropertySchemaAssociationFormData),
    remove,
    // Bulk CSV import needs positional inserts, which `append` cannot express.
    insert,
    move,
    /**
     * Entity-create with a parent: re-seed the mandatory schemas against what the parent provides.
     * `current` is the caller's `getValues("propertySchemaAssociations")` (uncontrolled inputs may lag in `fields`).
     */
    reconcileMandatorySchemas: (
      current: readonly PropertySchemaAssociationFormData[],
      inherited: readonly InheritedSchemaSummary[],
    ) => {
      replace(reconcileMandatoryAssociations(current, inherited));
    },
    setDefaults: (associations: PropertySchemaAssociation_DefaultFragment[]) => {
      const data = associations
        .map(toAssociationFormData)
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((item, index) => ({ ...item, position: index }));
      replace(data as PropertySchemaAssociationFormData[]);
    },
  };
}
